import dayjs from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Text, Textarea } from 'rizzui';
import { toast } from 'sonner';
import CustomSelect from '../../../../../components/forms/CustomSelect';
import ModalForm from '../../../../../components/forms/ModalForm';
import { Loader } from '../../../../../components/loader/SpinnerLogo';
import { FilterDrawerView } from '../../../../../components/table/components/TableFilter';
import CustomTable from '../../../../../components/table/CustomTable';
import useFetch from '../../../../../hooks/useFetch';
import useFilters from '../../../../../hooks/useFilters';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import { FacilitatorCallsService } from '../../../../../services/calls/facilitatorCallsService';
import ContractProfileLayoutWrapper from '../../ContractProfileLayoutWrapper';

const trackingColumnOrder = [
    'createdAt',
    'classificationName',
    'stageLabel',
    'scheduledAt',
    'userName',
    'automaticIncidenceCode',
    'notes',
];

const stageOptions = [
    { value: 'start', label: 'Inicio' },
    { value: 'follow_up', label: 'Seguimiento' },
    { value: 'end', label: 'Fin' },
    { value: 'completed', label: 'Completado' },
];

const stageLabels: Record<string, string> = {
    start: 'Inicio',
    follow_up: 'Seguimiento',
    end: 'Fin',
    completed: 'Completado',
};

const initialForm = {
    classificationId: '',
    notes: '',
    scheduledAt: '',
};

const getSelectRows = (payload: any): any[] => {
    if (Array.isArray(payload?.data?.data)) return payload.data.data;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload)) return payload;
    return [];
};

const formatDateTime = (value: any): string => {
    const rawValue = value?.date ?? value;
    if (!rawValue) return '-';
    const formatted = dayjs(rawValue);
    return formatted.isValid() ? formatted.format('DD/MM/YYYY HH:mm') : '-';
};

const ContractFacilitatorTracking = () => {
    const { id = '' } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const service = new FacilitatorCallsService();
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [openFilters, setOpenFilters] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [form, setForm] = useState(initialForm);
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFilters(
        { contract: id },
        [{ field: 'createdAt', order: 'desc' }],
        1,
        25,
        { contract: id },
    );

    const [trackingResponse, trackingLoading, trackingError, refetchTracking] = useFetch(useCallback(async () => {
        if (!id) return null;
        const response = await service.listContractTracking(filters);
        return response.getResponseData();
    }, [filters, id]));

    const [classificationResponse] = useFetch(useCallback(async () => {
        const response = await service.listClassificationOptions();
        return response.getResponseData();
    }, []));

    const trackingRows = Array.isArray(trackingResponse?.data) ? trackingResponse.data : [];
    const classifications = getSelectRows(classificationResponse);
    const classificationOptions = classifications.map((classification: any) => ({
        value: classification.id,
        label: classification.name,
        requiresSchedule: Boolean(classification.requiresSchedule),
    }));
    const selectedClassification = classificationOptions.find((classification) => classification.value === form.classificationId) || null;
    const requiresSchedule = Boolean(selectedClassification?.requiresSchedule);
    const lastPage = trackingResponse?.lastPage ?? 1;
    const totalRegisters = trackingResponse?.totalRegisters ?? trackingRows.length;

    const closeFormModal = () => {
        setIsFormModalOpen(false);
        setForm(initialForm);
    };

    const orderedData = useMemo(() => trackingRows.map((row: any) => ({
        id: row.id,
        createdAt: row.createdAt,
        classificationName: row.classification?.name || 'Sin tipificación',
        stageLabel: stageLabels[row.stage] || row.stage || '-',
        scheduledAt: row.scheduledAt,
        userName: [row.user?.name, row.user?.lastName].filter(Boolean).join(' ') || 'Sin usuario',
        automaticIncidenceCode: row.automaticIncidence?.code || '',
        notes: row.notes || '',
        raw: row,
    })), [trackingRows]);

    const handleCreateTracking = async () => {
        if (!form.classificationId) {
            toast.error('Debes seleccionar una tipificación.');
            return;
        }

        if (requiresSchedule && !form.scheduledAt) {
            toast.error('Debes indicar la fecha y hora de agenda.');
            return;
        }

        setIsSaving(true);

        try {
            const response = await (await service.createContractTracking({
                contractId: id,
                classificationId: form.classificationId,
                notes: form.notes.trim() || null,
                scheduledAt: requiresSchedule && form.scheduledAt ? form.scheduledAt : null,
            })).getResponseData();

            if (!response?.success) {
                handleErrors(response);
                return;
            }

            toast.success('Seguimiento registrado correctamente');
            closeFormModal();
            refetchTracking();
        } catch (error) {
            handleErrors(error);
        } finally {
            setIsSaving(false);
        }
    };

    if (trackingLoading && !trackingResponse) {
        return (
            <ContractProfileLayoutWrapper isLoading>
                <Loader height="35vh" />
            </ContractProfileLayoutWrapper>
        );
    }

    return (
        <ContractProfileLayoutWrapper>
            <div className="pt-4">
                {trackingError ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        No se ha podido cargar la sección de seguimiento para este contrato.
                    </div>
                ) : (
                    <>
                        <CustomTable
                            id="contract-tracking-table"
                            columnOrder={trackingColumnOrder}
                            data={orderedData}
                            isLoading={trackingLoading}
                            filters={filters}
                            updateFilters={updateFilters}
                            updateFilterOrder={updateFilterOrder}
                            defaultOrder={filters.filter_order || undefined}
                            paginationData={{
                                pageSize: filters.limit,
                                currentPage: filters.page,
                                pageCount: lastPage,
                                totalCount: totalRegisters,
                                handlePagination: updatePage,
                                handlePerPage: updatePageSize,
                            }}
                            toggleFilters={() => setOpenFilters(!openFilters)}
                            columnsNotShown={[
                                'raw'
                            ]}
                            overrideColumns={[
                                {
                                    key: 'classificationName',
                                    render: (row: any) => <Text className="font-bold text-primary">{row.row.original.classificationName}</Text>,
                                },
                                {
                                    key: 'createdAt',
                                    render: (row: any) => formatDateTime(row.row.original.createdAt),
                                },
                                {
                                    key: 'scheduledAt',
                                    render: (row: any) => row.row.original.scheduledAt ? formatDateTime(row.row.original.scheduledAt) : 'Sin agenda',
                                },
                                {
                                    key: 'stageLabel',
                                    render: (row: any) => (
                                        <span className="inline-flex rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-700">
                                            {row.row.original.stageLabel}
                                        </span>
                                    ),
                                },
                                {
                                    key: 'automaticIncidenceCode',
                                    render: (row: any) => row.row.original.automaticIncidenceCode || 'No',
                                },
                                {
                                    key: 'notes',
                                    render: (row: any) => (
                                        <div className="max-w-[28rem] whitespace-pre-wrap text-sm text-gray-700">
                                            {row.row.original.notes || 'Sin observaciones'}
                                        </div>
                                    ),
                                },
                            ]}
                        >
                            <div className="flex items-center gap-2">
                                <Button onClick={() => setIsFormModalOpen(true)}>
                                    Registrar seguimiento
                                </Button>
                                <Button variant="outline" onClick={() => refetchTracking()}>
                                    Actualizar
                                </Button>
                            </div>
                        </CustomTable>

                        <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle="Filtros seguimiento">
                            <CustomSelect
                                id="contractTrackingClassificationFilter"
                                label="Tipificación"
                                isClearable
                                isSearchable
                                options={classificationOptions}
                                value={classificationOptions.find((option) => option.value === filters.filter_filters?.classification) || null}
                                onChange={(selected: any) => updateFilters({ classification: selected?.value || null })}
                            />

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700">Etapa</label>
                                <select
                                    className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm"
                                    value={filters.filter_filters?.stage || ''}
                                    onChange={(event) => updateFilters({ stage: event.target.value || null })}
                                >
                                    <option value="">Todas</option>
                                    {stageOptions.map((stage) => (
                                        <option key={stage.value} value={stage.value}>
                                            {stage.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <label className="flex items-center gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={Boolean(filters.filter_filters?.scheduled_only)}
                                    onChange={(event) => updateFilters({ scheduled_only: event.target.checked ? true : null })}
                                />
                                Solo con agenda
                            </label>

                            <Button
                                size="sm"
                                variant="flat"
                                className="h-9 bg-gray-200/70"
                                onClick={() => resetFilters(25)}
                            >
                                Limpiar
                            </Button>
                        </FilterDrawerView>

                        <ModalForm
                            isOpen={isFormModalOpen}
                            onClose={closeFormModal}
                            title="Registrar seguimiento"
                            size="lg"
                        >
                            <div className="space-y-4">
                                <CustomSelect
                                    id="contractTrackingClassification"
                                    label="Tipificación"
                                    required
                                    isSearchable
                                    options={classificationOptions}
                                    value={selectedClassification}
                                    onChange={(selected: any) => setForm((current) => ({
                                        ...current,
                                        classificationId: selected?.value || '',
                                        scheduledAt: selected?.requiresSchedule ? current.scheduledAt : '',
                                    }))}
                                />

                                {requiresSchedule && (
                                    <div>
                                        <label className="mb-1.5 block text-sm font-medium text-gray-700">Fecha y hora de agenda</label>
                                        <input
                                            className="h-10 w-full rounded-md border border-gray-300 px-3 text-sm"
                                            type="datetime-local"
                                            value={form.scheduledAt}
                                            onChange={(event) => setForm((current) => ({ ...current, scheduledAt: event.target.value }))}
                                        />
                                    </div>
                                )}

                                <Textarea
                                    label="Observaciones"
                                    value={form.notes}
                                    onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                                    rows={5}
                                />

                                <div className="flex justify-end gap-2">
                                    <Button variant="outline" onClick={closeFormModal}>
                                        Cancelar
                                    </Button>
                                    <Button onClick={handleCreateTracking} disabled={isSaving || !form.classificationId}>
                                        {isSaving ? 'Guardando...' : 'Guardar seguimiento'}
                                    </Button>
                                </div>
                            </div>
                        </ModalForm>
                    </>
                )}
            </div>
        </ContractProfileLayoutWrapper>
    );
};

export default ContractFacilitatorTracking;
